import { ref } from 'vue'
import { readSessionTokenForHandle } from '../composables/useAccountBook.ts'

/**
 * Which of the signed in accounts this tab is acting as.
 *
 * It is set from the account handle in the tab's own URL, never from shared state, which
 * is precisely what lets two tabs hold two different accounts at the same time: they share
 * the account book in web storage, but each one sends only its own token.
 */
const activeAccountHandleReference = ref<string | null>(null)

/**
 * A token that has just been handed to the application and does not belong to a known
 * account yet: the very first request it makes is the one that establishes whose it is.
 */
const unattributedSessionTokenReference = ref<string | null>(null)

export function setActiveAccountHandle(accountHandle: string | null): void {
  activeAccountHandleReference.value = accountHandle
  unattributedSessionTokenReference.value = null
}

export function useUnattributedSessionToken(sessionToken: string | null): void {
  unattributedSessionTokenReference.value = sessionToken
  activeAccountHandleReference.value = null
}

export function readActiveAccountHandle(): string | null {
  return activeAccountHandleReference.value
}

export function readActiveSessionToken(): string | null {
  if (unattributedSessionTokenReference.value !== null) {
    return unattributedSessionTokenReference.value
  }
  const activeAccountHandle = activeAccountHandleReference.value
  return activeAccountHandle === null ? null : readSessionTokenForHandle(activeAccountHandle)
}

/** The header every authenticated request carries, or nothing when no account is active. */
export function buildAuthorizationHeaders(): Record<string, string> {
  const sessionToken = readActiveSessionToken()
  return sessionToken === null ? {} : { authorization: `Bearer ${sessionToken}` }
}
